<script setup lang="ts">
import { useKpiStore, type KpiTab } from '@/stores/useKpiStore'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import AvatarMultiselect from '@/components/common/selects/AvatarMultiselect.vue'
import { storeToRefs } from 'pinia'
import ChartSaleOrderKpi from '@/components/data-visualization/charts/ChartSaleOrderKpi.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ChartOpportunityKpi from '@/components/data-visualization/charts/ChartOpportunityKpi.vue'
import ChartEventKpi from '@/components/data-visualization/charts/ChartEventKpi.vue'
import ChartCallKpi from '@/components/data-visualization/charts/ChartCallKpi.vue'
import Cookies from 'js-cookie'

const kpiStore = useKpiStore()
const { usersSelected, startDate, endDate, viewType, kpiCommercials, activeTab } = storeToRefs(kpiStore)
const COOKIE_NAME = 'select_view_kpi'
const COOKIE_EXPIRY = 365 * 10
const FILTER_DEBOUNCE_MS = 400

let refreshTimeout: ReturnType<typeof setTimeout> | undefined

// Loads the current tab first (awaited), then background-prefetches the others.
// On filter changes we force a refresh; on tab clicks / mount we rely on cache TTL.
const loadActiveThenPrefetch = (options: { force?: boolean } = {}) => {
  kpiStore.fetchAllTabsWithPriority(activeTab.value, options)
}

const scheduleRefresh = () => {
  if (refreshTimeout !== undefined) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(() => {
    refreshTimeout = undefined
    kpiStore.invalidateCaches()
    loadActiveThenPrefetch({ force: true })
  }, FILTER_DEBOUNCE_MS)
}

watch(
  [startDate, endDate, usersSelected],
  ([newStartDate, newEndDate], [oldStartDate, oldEndDate]) => {
    // Si la date de début change et devient supérieure à la date de fin
    if (newStartDate !== oldStartDate && newStartDate && newEndDate && newStartDate > newEndDate) {
      endDate.value = ''
    }

    // Si la date de fin change et devient inférieure à la date de début
    if (newEndDate !== oldEndDate && newStartDate && newEndDate && newEndDate < newStartDate) {
      startDate.value = ''
    }

    if (startDate.value && endDate.value && usersSelected.value?.length) {
      scheduleRefresh()
    }
  },
)

onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME)
  if (savedView) {
    try {
      viewType.value = JSON.parse(savedView)
    } catch {
      viewType.value = 'month'
    }
  } else {
    viewType.value = 'month'
  }
  if (!startDate.value || !endDate.value) {
    kpiStore.initializeDateRange()
  }
  if (usersSelected.value?.length) {
    loadActiveThenPrefetch()
  }
})

onBeforeUnmount(() => {
  if (refreshTimeout !== undefined) clearTimeout(refreshTimeout)
})

function changeTab() {
  window.dispatchEvent(new Event('resize'))
}

const handleTabChange = (tab: KpiTab) => {
  activeTab.value = tab
  changeTab()
  // Cache-aware: no-op if this tab was already prefetched within the TTL
  kpiStore.fetchKpiForTab(tab)
}

const handleViewUpdate = (value: 'day' | 'week' | 'month' | 'year') => {
  viewType.value = value
  // Enregistrer la sélection dans un cookie qui n'est pas supprimé à la fermeture du navigateur et qui n'expire pas
  Cookies.set(COOKIE_NAME, JSON.stringify(viewType.value), {
    expires: COOKIE_EXPIRY,
    secure: true,
  })
}

const kpiCommercialsOptions = computed(() =>
  kpiCommercials.value.map((c) => ({
    id: c.id,
    text: c.name,
    subText: c.email || '',
  })),
)
</script>

<template>
  <div class="w-full flex flex-col gap-4 p-8">
    <div class="flex flex-col gap-12">
      <Bs2Title title="KPI" />
    </div>
    <div class="card bg-white rounded-lg p-4">
      <div class="grid grid-cols-12 mb-4">
        <!-- Date range selectors -->
        <div class="col-span-6 mb-4 flex items-center gap-4">
          <div class="flex items-center">
            <label for="start-date" class="mr-2 font-medium">Date de début:</label>
            <input
              type="date"
              id="start-date"
              v-model="startDate"
              class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex items-center">
            <label for="end-date" class="mr-2 font-medium">Date de fin:</label>
            <input
              type="date"
              id="end-date"
              v-model="endDate"
              class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <!-- View type buttons -->
        <div class="col-span-6 mb-4 flex justify-end items-center gap-2">
          <label class="mr-2 font-medium">Type de vue:</label>
          <ButtonDaisy
            @click="handleViewUpdate('day')"
            :text="'Jour'"
            :variant="viewType === 'day' ? 'primary' : 'default'"
            size="sm"
          />
          <ButtonDaisy
            @click="handleViewUpdate('week')"
            :text="'Semaine'"
            :variant="viewType === 'week' ? 'primary' : 'default'"
            size="sm"
          />
          <ButtonDaisy
            @click="handleViewUpdate('month')"
            :text="'Mois'"
            :variant="viewType === 'month' ? 'primary' : 'default'"
            size="sm"
          />
          <ButtonDaisy
            @click="handleViewUpdate('year')"
            :text="'Année'"
            :variant="viewType === 'year' ? 'primary' : 'default'"
            size="sm"
          />
        </div>
        <div class="col-span-12">
          <AvatarMultiselect
            v-model="usersSelected"
            :options="kpiCommercialsOptions"
            unit="utilisateur"
            unit-plural="utilisateurs"
          />
        </div>
      </div>
    </div>
    <div class="tabs tabs-lift text-center justify-center">
      <input
        type="radio"
        name="my_tabs_3"
        class="tab"
        aria-label="Devis"
        :checked="activeTab === 'saleOrder'"
        @change="handleTabChange('saleOrder')"
      />
      <div class="tab-content bg-base-100 border-base-300 p-6">
        <div class="flex-column space-y-4">
          <ChartSaleOrderKpi
            :key="`sale-order-${startDate}-${endDate}-${usersSelected}`"
            :view-type="viewType"
          ></ChartSaleOrderKpi>
        </div>
      </div>

      <input
        type="radio"
        name="my_tabs_3"
        class="tab"
        aria-label="Affaires"
        :checked="activeTab === 'opportunity'"
        @change="handleTabChange('opportunity')"
      />
      <div class="tab-content bg-base-100 border-base-300 p-6">
        <div class="flex-column space-y-4">
          <ChartOpportunityKpi
            :key="`opportunity-${startDate}-${endDate}-${usersSelected}`"
            :view-type="viewType"
          ></ChartOpportunityKpi>
        </div>
      </div>
      <input
        type="radio"
        name="my_tabs_3"
        class="tab"
        aria-label="Rendez-vous"
        :checked="activeTab === 'event'"
        @change="handleTabChange('event')"
      />
      <div class="tab-content bg-base-100 border-base-300 p-6">
        <div class="flex-column space-y-4">
          <ChartEventKpi
            :key="`event-${startDate}-${endDate}-${usersSelected}`"
            :view-type="viewType"
          ></ChartEventKpi>
        </div>
      </div>
      <input
        type="radio"
        name="my_tabs_3"
        class="tab"
        aria-label="Appels"
        :checked="activeTab === 'call'"
        @change="handleTabChange('call')"
      />
      <div class="tab-content bg-base-100 border-base-300 p-6">
        <div class="flex-column space-y-4">
          <ChartCallKpi
            :key="`call-${startDate}-${endDate}-${usersSelected}`"
            :view-type="viewType"
          ></ChartCallKpi>
        </div>
      </div>
    </div>
  </div>
</template>

